import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarPlus, LayoutGrid, List, Columns3, Search, CalendarX2 } from 'lucide-react';
import { type Event, EventStatus, isAdmin } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import { useEvents, useEventTypes, useEventOverviews, useUpdateEvent } from '../hooks/useEvents';
import { useVendors } from '../hooks/useVendors';
import { EntityList, FilterSelect } from '../components/common';
import type { EntityListColumn, SortDirection } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import { SegmentedControl } from '../components/common/SegmentedControl';
import { CreateEventPanel, EventLogo } from '../components/events';
import { EventCard } from '../components/events/EventCard';
import { EventBoard } from '../components/events/EventBoard';
import { EventCountdown } from '../components/events/EventCountdown';
import { EventStatusBadge } from '../components/events/EventStatusBadge';
import { ReadinessRing } from '../components/events/ReadinessRing';
import { EVENT_STATUS_OPTIONS, eventStatusLabel } from '../components/events/eventStatus';
import { RecordConversations } from '../components/messaging/RecordConversations';
import { useThreadCounts } from '../hooks/useThreadCounts';
import { computeEventReadiness } from '@peles/shared';
import {
  DATE_RANGE_OPTIONS,
  formatShortDate,
  inDateRange,
  type DateRangeKey,
} from '../utils/eventDates';

type ViewMode = 'cards' | 'table' | 'board';

const PAGE_SIZE = 24;
const VIEW_STORAGE_KEY = 'events:view';

const VIEW_OPTIONS: { id: ViewMode; label: string; icon: JSX.Element }[] = [
  { id: 'cards', label: 'Cards', icon: <LayoutGrid size={14} /> },
  { id: 'table', label: 'Table', icon: <List size={14} /> },
  { id: 'board', label: 'Board', icon: <Columns3 size={14} /> },
];

const ALL_STATUSES = EVENT_STATUS_OPTIONS.map((s) => s.id);

/**
 * Statuses shown when the address names none. Looking ahead, a completed or
 * cancelled event is noise; looking back (Past, All) completed events are the
 * point, so only cancelled ones stay hidden.
 */
export function defaultStatusesFor(range: DateRangeKey): EventStatus[] {
  const hidden: EventStatus[] =
    range === 'past' || range === 'all'
      ? [EventStatus.CANCELLED]
      : [EventStatus.COMPLETED, EventStatus.CANCELLED];
  return ALL_STATUSES.filter((s) => !hidden.includes(s));
}

function readView(param: string | null): ViewMode {
  if (param === 'cards' || param === 'table' || param === 'board') return param;
  try {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === 'cards' || saved === 'table' || saved === 'board') return saved;
  } catch {
    // storage unavailable — fall through to the default
  }
  return 'cards';
}

function readList(param: string | null): string[] | null {
  return param === null ? null : param.split(',').filter(Boolean);
}

/** `?status=all` is a cleared Status filter: every status, cancelled included. */
function readStatuses(param: string | null, range: DateRangeKey): string[] {
  if (param === 'all') return ALL_STATUSES;
  return readList(param) ?? defaultStatusesFor(range);
}

const SORT_ACCESSORS: Record<string, (event: Event) => string | number | null> = {
  name: (e) => e.name,
  date: (e) => (e.startAt ? new Date(e.startAt).getTime() : null),
  venue: (e) => e.venueName,
  status: (e) => eventStatusLabel(e.status),
};

function sortEvents(events: Event[], sortBy: string | null, sortDir: SortDirection | null) {
  const accessor = sortBy ? SORT_ACCESSORS[sortBy] : undefined;
  if (!accessor || !sortDir) return events;
  const sign = sortDir === 'asc' ? 1 : -1;
  return [...events].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    // Missing values always sort last, whichever the direction.
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return sign * (va - vb);
    return (
      sign * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' })
    );
  });
}

export function EventsPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { confirm, alert } = useDialog();
  const [params, setParams] = useSearchParams();
  const updateEvent = useUpdateEvent();

  const view = readView(params.get('view'));
  const range = (DATE_RANGE_OPTIONS.find((r) => r.id === params.get('range'))?.id ??
    'upcoming') as DateRangeKey;
  const statusFilter = readStatuses(params.get('status'), range);
  const typeFilter = readList(params.get('type')) ?? [];
  const venueFilter = readList(params.get('venue')) ?? [];
  const vendorFilter = readList(params.get('vendor')) ?? [];
  const search = params.get('q') ?? '';
  const showCreate = params.get('new') === '1';
  const [page, setPage] = useState(1);
  // Past events read newest first; everything else soonest first.
  const [sort, setSort] = useState<{ by: string | null; dir: SortDirection | null }>({
    by: 'date',
    dir: range === 'past' ? 'desc' : 'asc',
  });

  // Which events a vendor is on is the server's to say (its bookings and
  // booked lines), so the vendor filter is the one asked of the server.
  const { data: everyEvent = [], isLoading: loadingEvery } = useEvents();
  const vendorQuery = useEvents(vendorFilter.length > 0 ? { vendorIds: vendorFilter } : undefined);
  const allEvents = vendorFilter.length > 0 ? (vendorQuery.data ?? []) : everyEvent;
  const isLoading = vendorFilter.length > 0 ? vendorQuery.isLoading : loadingEvery;
  // A vendor's events that could not be read are not "no events": say so.
  const vendorLoadFailed = vendorFilter.length > 0 && vendorQuery.isError && !vendorQuery.data;
  const { data: vendors = [] } = useVendors();
  const vendorOptions = useMemo(
    () =>
      vendors
        .map((v) => ({ id: v.id, name: v.businessName }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [vendors],
  );
  const { data: eventTypes = [] } = useEventTypes();
  // The venues events are held at — a venue with none has nothing to filter to.
  // Read from every event, so a venue stays on offer (and can be unticked)
  // while a vendor filter hides its events.
  const venueOptions = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const e of everyEvent) {
      if (e.venueId && !byId.has(e.venueId)) {
        byId.set(e.venueId, { id: e.venueId, name: e.venueName ?? 'Unnamed venue' });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [everyEvent]);

  /** Change some params, dropping any set back to its default. */
  const patchParams = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setParams],
  );

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      // storage unavailable — the choice just isn't remembered
    }
  }, [view]);

  const statusKey = statusFilter.join(',');
  const typeKey = typeFilter.join(',');
  const venueKey = venueFilter.join(',');
  const filtered = useMemo(() => {
    const statuses = statusKey.split(',');
    const types = typeKey ? typeKey.split(',') : [];
    const venues = venueKey ? venueKey.split(',') : [];
    const term = search.trim().toLowerCase();
    return allEvents.filter((e) => {
      if (!statuses.includes(e.status)) return false;
      if (types.length > 0 && !types.includes(e.eventTypeId)) return false;
      if (venues.length > 0 && (!e.venueId || !venues.includes(e.venueId))) return false;
      if (!inDateRange(e.startAt, range)) return false;
      if (term) {
        const hay = [e.name, e.venueName, e.eventTypeName].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    // The filters are rebuilt from the URL each render, so key on their strings.
  }, [allEvents, statusKey, typeKey, venueKey, range, search]);

  const sorted = useMemo(() => sortEvents(filtered, sort.by, sort.dir), [filtered, sort]);
  const pageItems =
    view === 'board' ? sorted : sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Cards and table ask for their page's numbers; the board asks for the cards
  // each of its columns is showing.
  const { data: overviewList = [] } = useEventOverviews(
    view === 'board' ? [] : pageItems.map((e) => e.id),
  );
  const overviews = useMemo(() => new Map(overviewList.map((o) => [o.eventId, o])), [overviewList]);
  const { countFor } = useThreadCounts('Event', view === 'table' ? pageItems.map((e) => e.id) : []);

  const moveEvent = async (event: Event, status: EventStatus) => {
    if (status === EventStatus.CANCELLED && !isAdmin(currentUser!.systemRole)) {
      void alert({
        title: 'Only administrators can cancel events',
        message: 'Ask an administrator to cancel this event.',
        variant: 'warning',
      });
      return;
    }
    if (status === EventStatus.CANCELLED) {
      const ok = await confirm({
        title: `Cancel ${event.name}?`,
        message: 'It will be hidden from the default events list and dashboard.',
        confirmLabel: 'Cancel event',
        cancelLabel: 'Keep it',
        variant: 'danger',
      });
      if (!ok) return;
    }
    updateEvent.mutate(
      { id: event.id, updates: { status } },
      {
        onError: () => {
          void alert({
            title: 'Status not changed',
            message: 'Please try again.',
            variant: 'error',
          });
        },
      },
    );
  };

  const columns: EntityListColumn<Event>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Event',
        sortable: true,
        render: (event) => (
          <div className="flex items-center gap-3">
            <EventLogo eventId={event.id} eventLogoUrl={event.logoUrl} sizeClassName="w-9 h-9" />
            <div className="min-w-0">
              <p className="text-gray-900 font-semibold flex items-center gap-1.5">
                <span className="truncate">{event.name}</span>
                <RecordConversations
                  entityType="Event"
                  entityId={event.id}
                  variant="icon"
                  count={countFor(event.id)}
                  // Its badge counts the event's records' conversations too;
                  // the tab lists them all.
                  onOpen={() => navigate(`/events/${event.id}/conversations`)}
                />
              </p>
              {event.eventTypeName && (
                <span className="text-xs text-primary-700">{event.eventTypeName}</span>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        width: 'w-36',
        render: (event) =>
          event.startAt ? (
            <div className="text-sm">
              <div className="text-gray-700">{formatShortDate(event.startAt)}</div>
              <EventCountdown startAt={event.startAt} className="mt-1 !px-2 !py-0.5 !text-[11px]" />
            </div>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        key: 'venue',
        header: 'Venue',
        sortable: true,
        hideOnMobile: true,
        render: (event) => <span className="text-sm text-gray-600">{event.venueName ?? '—'}</span>,
      },
      {
        key: 'guests',
        header: 'Guests',
        hideOnMobile: true,
        width: 'w-24',
        render: (event) => {
          const o = overviews.get(event.id);
          return (
            <span className="text-sm text-gray-700">
              {o ? `${o.guests.confirmed}/${o.guests.total}` : (event.guestCount ?? '—')}
            </span>
          );
        },
      },
      {
        key: 'owes',
        header: 'Client owes',
        hideOnMobile: true,
        width: 'w-28',
        render: (event) => {
          const o = overviews.get(event.id);
          if (!o) return <span className="text-gray-400">—</span>;
          const due = o.money.clientBalanceDue;
          return (
            <span
              className={`text-sm font-semibold ${due > 0.005 ? 'text-red-700' : 'text-green-700'}`}
            >
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(due)}
            </span>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        width: 'w-28',
        render: (event) => <EventStatusBadge status={event.status} />,
      },
      {
        key: 'ready',
        header: 'Ready',
        hideOnMobile: true,
        width: 'w-20',
        render: (event) => {
          const o = overviews.get(event.id);
          return <ReadinessRing score={o ? computeEventReadiness(o).score : null} size={34} />;
        },
      },
    ],
    // countFor and overviews change identity exactly when their data arrives.
    [countFor, overviews, navigate],
  );

  if (!currentUser) return null;

  const boardStatuses = ALL_STATUSES.filter((s) => statusFilter.includes(s));
  const rangeLabel = DATE_RANGE_OPTIONS.find((r) => r.id === range)!.label.toLowerCase();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isLoading
              ? 'Loading…'
              : vendorLoadFailed
                ? "Couldn't load"
                : `${filtered.length} ${filtered.length === 1 ? 'event' : 'events'} · ${rangeLabel}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => patchParams({ new: '1' })}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
        >
          <CalendarPlus size={16} />
          New event
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            variant="chips"
            ariaLabel="When"
            options={DATE_RANGE_OPTIONS}
            value={range}
            onChange={(r) => {
              patchParams({ range: r === 'upcoming' ? null : r });
              setSort({ by: 'date', dir: r === 'past' ? 'desc' : 'asc' });
            }}
          />
          <span className="hidden sm:block w-px h-6 bg-gray-200 mx-1" aria-hidden />
          <FilterSelect
            label="Status"
            multiple
            searchable={false}
            items={EVENT_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) => patchParams({ status: (value as string[]).join(',') || 'all' })}
            getValue={(s) => s.id}
            getLabel={(s) => s.name}
          />
          <FilterSelect
            label="Type"
            multiple
            items={eventTypes}
            value={typeFilter}
            onChange={(value) => patchParams({ type: (value as string[]).join(',') || null })}
            getValue={(t) => t.id}
            getLabel={(t) => t.name}
          />
          {(venueOptions.length > 0 || venueFilter.length > 0) && (
            <FilterSelect
              label="Venue"
              multiple
              items={venueOptions}
              value={venueFilter}
              onChange={(value) => patchParams({ venue: (value as string[]).join(',') || null })}
              getValue={(v) => v.id}
              getLabel={(v) => v.name}
            />
          )}
          <FilterSelect
            label="Vendor"
            multiple
            items={vendorOptions}
            value={vendorFilter}
            onChange={(value) => patchParams({ vendor: (value as string[]).join(',') || null })}
            getValue={(v) => v.id}
            getLabel={(v) => v.name}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="relative">
            <span className="sr-only">Search events</span>
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => patchParams({ q: e.target.value })}
              placeholder="Search events…"
              className="h-9 w-56 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </label>
          <SegmentedControl
            ariaLabel="View"
            options={VIEW_OPTIONS}
            value={view}
            onChange={(v) => patchParams({ view: v })}
          />
        </div>
      </div>

      {vendorLoadFailed && (
        <div
          role="alert"
          className="flex flex-col items-center text-center gap-2 py-16 bg-white rounded-2xl border border-red-200"
        >
          <p className="font-medium text-gray-800">This vendor's events could not be loaded</p>
          <button
            type="button"
            onClick={() => void vendorQuery.refetch()}
            className="h-9 px-4 rounded-lg border border-gray-200 bg-white text-sm font-medium hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      )}

      {view === 'table' && !vendorLoadFailed && (
        <EntityList
          items={pageItems}
          columns={columns}
          getKey={(event) => event.id}
          isLoading={isLoading}
          page={page}
          pageSize={PAGE_SIZE}
          total={sorted.length}
          onPageChange={setPage}
          sortBy={sort.by}
          sortDir={sort.dir}
          onSortChange={(by, dir) => {
            setSort({ by, dir });
            setPage(1);
          }}
          onRowClick={(event) => navigate(`/events/${event.id}`)}
          emptyMessage="No events match these filters"
        />
      )}

      {view !== 'table' && isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="events-skeleton">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-44 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      )}

      {view !== 'table' && !isLoading && !vendorLoadFailed && sorted.length === 0 && (
        <div className="flex flex-col items-center text-center gap-2 py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <CalendarX2 size={28} className="text-gray-300" />
          <p className="font-medium text-gray-800">No events match these filters</p>
          <p className="text-sm text-gray-500">Try another date range or status.</p>
        </div>
      )}

      {view === 'cards' && !isLoading && sorted.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pageItems.map((event) => (
              <EventCard key={event.id} event={event} overview={overviews.get(event.id)} />
            ))}
          </div>
          {sorted.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of{' '}
                {sorted.length}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="h-9 px-3 rounded-lg border border-gray-200 bg-white disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page * PAGE_SIZE >= sorted.length}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-9 px-3 rounded-lg border border-gray-200 bg-white disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {view === 'board' && !isLoading && sorted.length > 0 && (
        <EventBoard
          events={sorted}
          statuses={boardStatuses}
          onMove={(event, status) => void moveEvent(event, status)}
        />
      )}

      <EntityModal
        open={showCreate}
        onClose={() => patchParams({ new: null })}
        title="New Event"
        size="md"
      >
        <CreateEventPanel
          onClose={() => patchParams({ new: null })}
          onCreated={(event) => {
            patchParams({ new: null });
            if (event) navigate(`/events/${event.id}`);
          }}
        />
      </EntityModal>
    </div>
  );
}
